import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import ErrorBoundary from "@/shared/components/error-boundary";
import { ROUTES } from "@/shared/constants/routes";
import { Tab, Tabs } from "@nextui-org/react";
import clsx from "clsx";
import { Printer } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AGENT_TYPES, UploadDialogState } from "../../types";
import UploadDialog from "../upload-dialog";
import { AllThirdPartiesTab } from "./tabs/AllThirdPartiesTab";
import { BreachHistoryTab } from "./tabs/BreachHistoryTab";
import { OverviewTab } from "./tabs/OverviewTab";
import { RisksTab } from "./tabs/RisksTab";
import { SessionsTab } from "./tabs/SessionsTab";

export interface RisksFilterState {
  vendorName: string;
  severity: string;
}

const TPRADashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [totalVendors, setTotalVendors] = useState(0);
  const [isVendorsLoading, setIsVendorsLoading] = useState(true);
  const [risksFilter, setRisksFilter] = useState<RisksFilterState | null>(null);
  const [uploadDialogState, setUploadDialogState] = useState<UploadDialogState>({
    isOpen: false,
  });

  const handleTabChange = (key: React.Key) => {
    setActiveTab(key as string);
    // Clear filter when manually changing tabs
    if (key !== "risks") {
      setRisksFilter(null);
    }
  };

  const handleVendorCountChange = (count: number, isLoading: boolean) => {
    setTotalVendors(count);
    setIsVendorsLoading(isLoading);
  };

  const handleNavigateToRisks = (vendorName: string, severity: string) => {
    setRisksFilter({ vendorName, severity });
    setActiveTab("risks");
  };

  const handleStartNewAssessment = () => {
    setUploadDialogState({
      isOpen: true,
      type: AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
      sessionTitle: "",
    });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <ErrorBoundary fallback={null}>
      <div className="w-full h-full flex flex-col">
        <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px]">
          <div className="flex gap-4 items-center">
            <ZBreadCrumbs items={[{ title: "Third-Party Risk", href: `/${ROUTES.VENDORS}` }]} />
          </div>
          <ZButton color="primary" className="text-xs font-medium h-7 px-3" onClick={handleStartNewAssessment}>
            New Assessment
          </ZButton>
        </div>
        <div className="flex-1 overflow-hidden rounded-lg flex flex-col bg-white">
          <div className="px-4 pt-4 pb-2 flex flex-col gap-2">
            {/* header */}
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center flex-1 gap-2">
                <p className="text-sm font-medium leading-5 text-[#171717]">Third-Party Risk</p>
              </div>
              {/* <ZButton variant="bordered" className="h-6 px-3 text-xs font-medium" isDisabled>
                Export
              </ZButton> */}
              <button
                onClick={handlePrint}
                disabled={activeTab !== "overview"}
                className="flex flex-row items-center gap-1.5 w-8 h-6 px-2 py-1 bg-[#FAFAFA] border border-[#006FEE] rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Printer size={16} className="text-[#006FEE]" />
              </button>
            </div>
          </div>
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Tabs */}
            <div className="h-32px w-full border-b-1 border-[#E4E4E7]">
              <Tabs
                variant="underlined"
                selectedKey={activeTab}
                classNames={{
                  tab: "rounded-none px-4 text-[12px] text-[#18181B]",
                  base: "rounded-none",
                  cursor: "rounded-none",
                  panel: "rounded-none",
                  tabContent: "rounded-none",
                  tabList: "rounded-none p-0",
                  wrapper: "rounded-none",
                }}
                className="bg-white"
                onSelectionChange={handleTabChange}
              >
                <Tab
                  className={clsx("rounded-none", {
                    'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                      activeTab === "overview",
                  })}
                  key="overview"
                  title={
                    <div className="flex items-center gap-2">
                      <span className="text-[#18181B]">Summary</span>
                    </div>
                  }
                />
                <Tab
                  className={clsx("rounded-none", {
                    'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                      activeTab === "all-third-parties",
                  })}
                  key="all-third-parties"
                  title={
                    <div className="flex items-center gap-2">
                      <span className="text-[#18181B]">All Third-Parties</span>
                    </div>
                  }
                />
                <Tab
                  className={clsx("rounded-none", {
                    'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                      activeTab === "sessions",
                  })}
                  key="sessions"
                  title={
                    <div className="flex items-center gap-2">
                      <span className="text-[#18181B]">Sessions</span>
                    </div>
                  }
                />
                <Tab
                  className={clsx("rounded-none", {
                    'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                      activeTab === "breach-history",
                  })}
                  key="breach-history"
                  title={
                    <div className="flex items-center gap-2">
                      <span className="text-[#18181B]">Breach History</span>
                    </div>
                  }
                />
                
                <Tab
                  className={clsx("rounded-none", {
                    'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                      activeTab === "risks",
                  })}
                  key="risks"
                  title={
                    <div className="flex items-center gap-2">
                      <span className="text-[#18181B]">Risks</span>
                    </div>
                  }
                />

              </Tabs>
            </div>

            {/* Content Area */}
            <div
              className={clsx(
                "flex-1 p-4 pb-0 flex flex-col",
                activeTab === "all-third-parties" ? "overflow-hidden" : "overflow-auto",
              )}
            >
              {activeTab === "overview" && (
                <div className="h-full flex flex-col">
                  <h2 className="font-['Inter'] font-medium text-[14px] leading-[20px] text-[#11181C] mb-4">
                    Overview
                  </h2>
                  <OverviewTab onNavigateToBreachHistory={() => setActiveTab("breach-history")} />
                </div>
              )}

              {activeTab === "breach-history" && (
                <div className="h-full flex flex-col overflow-hidden">
                  <BreachHistoryTab />
                </div>
              )}

              {activeTab === "all-third-parties" && (
                <div className="h-full flex flex-col overflow-hidden">
                  <AllThirdPartiesTab 
                    onVendorCountChange={handleVendorCountChange} 
                    onSeverityClick={handleNavigateToRisks}
                  />
                </div>
              )}

              {activeTab === "risks" && (
                <div className="h-full flex flex-col overflow-hidden">
                  <RisksTab
                    initialFilter={risksFilter}
                    onFilterApplied={() => setRisksFilter(null)}
                  />
                </div>
              )}

              {activeTab === "sessions" && (
                <div className="h-full flex flex-col overflow-hidden">
                  <SessionsTab />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <UploadDialog
        data={uploadDialogState}
        setIsOpen={setUploadDialogState}
        onAddNewFramework={undefined}
      />
    </ErrorBoundary>
  );
};

export default TPRADashboard;
